import { zodResolver } from '@hookform/resolvers/zod'
import LoadingButton from '@mui/lab/LoadingButton'
import { Box, Button, FormHelperText, Stack, Typography } from '@mui/material'
import { createQueryRange, updateQueryRange } from 'api/query'
import FormInput from 'components/form-elements/FormInput'
import NotificationSys from 'components/NotificationSystem'
import { FIELD_IS_REQUIRED, minButtonWidth } from 'const'
import { UIModal } from 'features/UI'
import { useCharactersLeft } from 'hooks/useCharactersLeft'
import { useShowControl } from 'hooks/useShowControl'
import isEqual from 'lodash/isEqual'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { QueryRange } from 'types'
import { numberErrorMessage } from 'utils'
import { coerce, number, object, string, TypeOf } from 'zod'
import { QueryRangeArticle } from '../QueryRangeArticle/QueryRangeArticle'
import { QueryRangeCareArticle } from '../QueryRangeCareArticle/QueryRangeCareArticle'
import * as Styled from './QueryRangeModalForm.styled'

function QueryRangeModalFormContent({
  queryId,
  minPoints,
  maxPoints,
  range,
  ranges,
  onReloadQuery,
}: {
  queryId: number
  minPoints: number
  maxPoints: number
  range: QueryRange | null
  ranges: QueryRange[]
  onReloadQuery: () => Promise<void>
}) {
  const formSchema = object({
    id: number().optional(),
    name: string().optional(),
    text: string().min(1, FIELD_IS_REQUIRED).max(2000, 'Please enter a maximum of 2000 characters'),
    pointsFrom: coerce
      .number(numberErrorMessage)
      .int()
      .min(minPoints, `Lowest range value is ${minPoints}. Please revise values in your ranges`),
    pointsTo: coerce
      .number(numberErrorMessage)
      .int()
      .min(minPoints, `Lowest range value is ${minPoints}. Please revise values in your ranges`)
      .max(
        maxPoints,
        'A range value can not exceed the total maximum possible points for the query',
      ),
    learnArticleId: number().nullable().optional(),
    careArticleId: number().nullable().optional(),
  })
    .refine((data) => data.pointsFrom <= data.pointsTo, {
      message: "'Range To' must be greater or equals than 'Range From'",
      path: ['pointsTo'],
    })
    .refine(
      (data) => {
        return !(data.pointsTo !== maxPoints && data.learnArticleId === null)
      },
      {
        message: 'A link to a Learn Library Article is required',
        path: ['learnArticleId'],
      },
    )

  type FormType = TypeOf<typeof formSchema>

  const defaultRange = useMemo(() => {
    const emptyRange = {
      name: '',
      text: '',
      pointsFrom: 0,
      pointsTo: 0,
      learnArticleId: null,
      careArticleId: null,
    }

    if (!range) return emptyRange

    return {
      ...range,
      name: range.name || '',
    }
  }, [range])

  const formSettings = {
    mode: 'onChange' as const,
    resolver: zodResolver(formSchema),
    defaultValues: defaultRange,
  }

  const methods = useForm<FormType>(formSettings)

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    setValue,
    watch,
  } = methods

  const textValue = watch('text')

  const [isOpenConfirmArticle, onOpenConfirmArticle, onCloseConfirmArticle] = useShowControl()
  const [learnArticleId, setLearnArticleId] = useState<number | null>(null)

  const handleChangeArticle = useCallback(() => {
    setValue('learnArticleId', learnArticleId)
    setLearnArticleId(null)
    onCloseConfirmArticle()
  }, [learnArticleId, onCloseConfirmArticle, setValue])

  const handleCheckChangeArticle = useCallback(
    (value: number) => {
      const hasSameArticle = ranges
        .filter((rangeItem) => rangeItem.id !== range?.id)
        .find((rangeItem) => rangeItem.learnArticleId === value)
      if (hasSameArticle) {
        setLearnArticleId(value)
        onOpenConfirmArticle()
      } else {
        setValue('learnArticleId', value)
      }
    },
    [onOpenConfirmArticle, range, ranges, setValue],
  )

  const [isOpenConfirmCareArticle, onOpenConfirmCareArticle, onCloseConfirmCareArticle] =
    useShowControl()
  const [careArticleIdPending, setCareArticleIdPending] = useState<number | null>(null)

  const handleChangeCareArticle = useCallback(() => {
    setValue('careArticleId', careArticleIdPending)
    setCareArticleIdPending(null)
    onCloseConfirmCareArticle()
  }, [careArticleIdPending, onCloseConfirmCareArticle, setValue])

  const handleCheckChangeCareArticle = useCallback(
    (value: number) => {
      const hasSameArticle = ranges
        .filter((rangeItem) => rangeItem.id !== range?.id)
        .find((rangeItem) => rangeItem.careArticleId === value)
      if (hasSameArticle) {
        setCareArticleIdPending(value)
        onOpenConfirmCareArticle()
      } else {
        setValue('careArticleId', value)
      }
    },
    [onOpenConfirmCareArticle, range, ranges, setValue],
  )

  const handleSave = useCallback(
    async (values: FormType) => {
      try {
        if (range) {
          const { learnArticleName, careArticleName, careArticleType, order, ...rangeToCompare } =
            range
          if (!isEqual(values, rangeToCompare)) {
            await updateQueryRange({
              ...values,
              id: range.id,
              queryId,
            })
          }
        } else {
          await createQueryRange({
            ...values,
            queryId,
          })
        }

        await onReloadQuery()
      } catch (error) {
        NotificationSys.showWarning('An error occurred while saving the range')
      }
    },
    [onReloadQuery, queryId, range],
  )

  const charactersLeft = useCharactersLeft(2000, textValue.length)

  return (
    <FormProvider {...methods}>
      <Stack component="form" className="gap-16" flexGrow={1} onSubmit={handleSubmit(handleSave)}>
        <FormInput
          name="name"
          label="Range Name"
          placeholder="Enter Range Name"
          checkTouchField={false}
        />

        <Styled.PointsContainer>
          <FormInput
            type="number"
            name="pointsFrom"
            label="Range From"
            placeholder="Enter points"
            checkTouchField={false}
          />

          <FormInput
            type="number"
            name="pointsTo"
            label="Range To"
            placeholder="Enter points"
            checkTouchField={false}
          />
        </Styled.PointsContainer>

        <FormInput
          name="text"
          label="Range Text"
          placeholder="Start typing"
          fullWidth
          minRows={3}
          maxRows={6}
          multiline
          checkTouchField={false}
          helperText={charactersLeft}
        />

        <Controller
          control={control}
          name="learnArticleId"
          render={({ field, formState: { errors } }) => {
            return (
              <div>
                <QueryRangeArticle value={field.value} onChange={handleCheckChangeArticle} />
                {errors.learnArticleId && (
                  <FormHelperText error>{errors.learnArticleId.message}</FormHelperText>
                )}
              </div>
            )
          }}
        />

        <Controller
          control={control}
          name="careArticleId"
          render={({ field }) => {
            return (
              <QueryRangeCareArticle value={field.value} onChange={handleCheckChangeCareArticle} />
            )
          }}
        />

        <Box>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Save
          </LoadingButton>
        </Box>
      </Stack>

      <UIModal
        isOpen={isOpenConfirmArticle}
        title="Confirm Change Learn Library article"
        onClose={onCloseConfirmArticle}
      >
        <Stack>
          <Typography>
            Please note, this Learn Library article has already been added to a different result. If
            the Article should remain the same, please select &quot;Confirm&quot;.
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 5 }}>
            <Button variant="outlined" onClick={onCloseConfirmArticle} sx={minButtonWidth}>
              Close
            </Button>
            <Button
              variant="contained"
              type="button"
              sx={minButtonWidth}
              onClick={handleChangeArticle}
            >
              Confirm
            </Button>
          </Stack>
        </Stack>
      </UIModal>

      <UIModal
        isOpen={isOpenConfirmCareArticle}
        title="Confirm Change Action Post"
        onClose={onCloseConfirmCareArticle}
      >
        <Stack>
          <Typography>
            Please note, this TimeLine post has already been added to a different result. If the
            Article should remain the same, please select &quot;Confirm&quot;.
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 5 }}>
            <Button variant="outlined" onClick={onCloseConfirmCareArticle} sx={minButtonWidth}>
              Close
            </Button>
            <Button
              variant="contained"
              type="button"
              sx={minButtonWidth}
              onClick={handleChangeCareArticle}
            >
              Confirm
            </Button>
          </Stack>
        </Stack>
      </UIModal>
    </FormProvider>
  )
}

export function QueryRangeModalForm({
  isOpen,
  minPoints,
  maxPoints,
  queryId,
  range,
  ranges,
  onClose,
  onReloadQuery,
}: {
  isOpen: boolean
  minPoints: number
  maxPoints: number
  queryId: number
  range: QueryRange | null
  ranges: QueryRange[]
  onClose: () => void
  onReloadQuery: () => Promise<void>
}) {
  return (
    <UIModal
      isOpen={isOpen}
      title={!!range ? 'Edit Result Range' : 'Add Result Range'}
      onClose={onClose}
      width={800}
    >
      {isOpen ? (
        <QueryRangeModalFormContent
          queryId={queryId}
          range={range}
          ranges={ranges}
          minPoints={minPoints}
          maxPoints={maxPoints}
          onReloadQuery={onReloadQuery}
        />
      ) : (
        <Box />
      )}
    </UIModal>
  )
}
